import React, { useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  Container,
  ToggleButton,
  ToggleButtonGroup,
  Divider,
} from "@mui/material";
import { ArrowBack } from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import ParticipantLevelContent from "./ParticipantLevelContent";
import StudyLevelContent from "./StudyLevelContent";

type CoverageLevel = "study" | "participant";

interface DataCoverageDashboardProps {}

const DataCoverageDashboard: React.FC<DataCoverageDashboardProps> = () => {
  const { studyId } = useParams<{ studyId: string }>();
  const navigate = useNavigate();
  const [coverageLevel, setCoverageLevel] = useState<CoverageLevel>("study");

  const handleCoverageLevelChange = (
    _event: React.MouseEvent<HTMLElement>,
    newLevel: CoverageLevel | null,
  ) => {
    if (newLevel !== null) {
      setCoverageLevel(newLevel);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={handleBack}
          sx={{ textTransform: "none" }}
        >
          Back
        </Button>
        <Typography variant="h4" sx={{ fontWeight: 600 }}>
          Data Coverage
        </Typography>
      </Box>

      <Divider sx={{ mb: 3 }} />

      {/* Coverage Level Selector */}
      <Paper
        sx={{
          p: 3,
          mb: 3,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 2,
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
          Select Coverage Level
        </Typography>

        <ToggleButtonGroup
          value={coverageLevel}
          exclusive
          onChange={handleCoverageLevelChange}
          sx={{
            "& .MuiToggleButton-root": {
              px: 3,
              py: 1,
              color: "#8C1515",
              border: "1px solid #8C1515",
              "&.Mui-selected": {
                backgroundColor: "#8C1515",
                color: "#FFFFFF",
                "&:hover": {
                  backgroundColor: "#B33A3A",
                },
              },
            },
          }}
        >
          <ToggleButton value="study">Study Level</ToggleButton>
          <ToggleButton value="participant">Participant Level</ToggleButton>
        </ToggleButtonGroup>

        <Box
          sx={{
            mt: 2,
            p: 2,
            bgcolor: "background.default",
            borderRadius: 1,
            textAlign: "center",
          }}
        >
          <Typography variant="body2" color="textSecondary">
            Current selection:{" "}
            <strong>
              {coverageLevel === "study" ? "Study Level" : "Participant Level"}
            </strong>
          </Typography>
          <Typography
            variant="caption"
            color="textSecondary"
            sx={{ display: "block", mt: 1 }}
          >
            Recommendation: For non-study-period or historical ranges, run data
            extraction first to ensure complete data availability before
            coverage analysis.
          </Typography>
        </Box>
      </Paper>

      {/* Content Area - Dynamic based on coverage level */}
      {coverageLevel === "study" ? (
        <StudyLevelContent studyId={studyId || ""} />
      ) : (
        <ParticipantLevelContent studyId={studyId || ""} />
      )}
    </Container>
  );
};

export default DataCoverageDashboard;
